import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { getDeals } from "../mock";
import { chatCompletion } from "../services/ai";
import { ArrowLeft, Globe, Factory, DollarSign, TrendingUp, Users, Calendar } from "lucide-react";

const TEXT = "text-[#383838]";
const MUTED = "text-[#616161]";
const CARD = "bg-white border border-gray-200 shadow-sm";
const LABEL = "text-xs font-medium text-[#616161] uppercase tracking-wide mb-1";

export default function DealDetails() {
  const { id } = useParams();
  const nav = useNavigate();
  const deals = getDeals();
  const deal = useMemo(() => deals.find(d => d.id === id), [id, deals]);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);

  if (!deal) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button className="bg-gray-100 hover:bg-gray-200 text-[#383838] border border-gray-200 h-8 text-sm" onClick={() => nav(-1)}>
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <h2 className={`text-lg font-semibold ${TEXT}`}>Deal not found</h2>
        </div>
        <div className={`text-sm ${MUTED}`}>We couldn't find a deal with id {id}.</div>
      </div>
    );
  }

  const meta = [
    { k: "Sector", v: deal.sector, icon: <Factory size={14} className="text-[#0078C1]" /> },
    { k: "Stage", v: deal.stage },
    { k: "Geography", v: deal.geography || "MEA", icon: <Globe size={14} className="text-emerald-500" /> },
    { k: "Owners", v: deal.owners || "Founder-led", icon: <Users size={14} className={MUTED} /> },
    { k: "EV ($M)", v: deal.enterpriseValue, icon: <DollarSign size={14} className="text-amber-500" /> },
    { k: "EBITDA Margin", v: `${deal.ebitdaMargin}%`, icon: <TrendingUp size={14} className="text-emerald-500" /> },
    { k: "Revenue YoY", v: `${deal.revenueYoY}%` },
    { k: "Updated", v: new Date(deal.updatedAt).toLocaleDateString(), icon: <Calendar size={14} className={MUTED} /> },
  ];

  const makeSummary = async () => {
    setBusy(true);
    try {
      const prompt = `Draft a concise investor brief for this deal.\nName: ${deal.name}\nSector: ${deal.sector}\nStage: ${deal.stage}\nEV: ${deal.enterpriseValue}M\nEBITDA margin: ${deal.ebitdaMargin}%\nRevenue YoY: ${deal.revenueYoY}%\nGeography: ${deal.geography || "MEA"}\nOwnership: ${deal.owners || "Founder-led"}\n\nInclude: investment thesis, key risks, diligence priorities, and near-term actions.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are an M&A associate at GII. Be crisp and structured." });
      setSummary(content);
    } catch (e) {
      setSummary(`Error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const scoreColor = (s) => s >= 85 ? "text-emerald-600 bg-emerald-50" : s >= 70 ? "text-[#0078C1] bg-blue-50" : "text-[#616161] bg-gray-100";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button className="bg-gray-100 hover:bg-gray-200 text-[#383838] border border-gray-200 h-8 text-sm" onClick={() => nav(-1)}>
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <h2 className={`text-xl font-bold ${TEXT}`}>{deal.name}</h2>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-[#0078C1]">{deal.sector}</span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-[#616161]">{deal.stage}</span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ml-auto ${scoreColor(deal.score)}`}>Score: {deal.score}</span>
      </div>

      {/* Deal Meta */}
      <Card className={CARD}>
        <CardContent className="p-5">
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-semibold uppercase tracking-wide ${MUTED}`}>Deal Score</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${scoreColor(deal.score)}`}>{deal.score}/100</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="h-2 rounded-full bg-[#0078C1] transition-all" style={{ width: `${deal.score}%` }} />
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-4 pt-3 border-t border-gray-100">
            {meta.map((m, i) => (
              <div key={i}>
                <div className={`${LABEL} flex items-center gap-1`}>{m.icon}{m.k}</div>
                <div className={`text-sm font-medium ${TEXT}`}>{m.v || "—"}</div>
              </div>
            ))}
          </div>
          {deal.description && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className={LABEL}>Description</div>
              <div className={`text-sm ${MUTED} leading-relaxed`}>{deal.description}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Summary */}
      <Card className={CARD}>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm font-semibold ${TEXT}`}>AI Investor Brief</div>
              <div className={`text-xs ${MUTED}`}>AI-generated investment summary and diligence priorities</div>
            </div>
            <Button
              className="bg-[#0078C1] hover:bg-[#005A91] text-white h-9 text-sm"
              onClick={makeSummary}
              disabled={busy}
            >
              {busy ? "Generating…" : "Generate Brief"}
            </Button>
          </div>
          <div className={`text-sm whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-32 ${TEXT} leading-relaxed`}>
            {summary || "Click Generate Brief to draft a concise investor summary with thesis, risks, and diligence priorities."}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
